import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { locationsApi } from '../../../api/admin/locations';
import { ApiError } from '../../../api/client';
import { IconActionButton } from '../../../components/IconActionButton';
import { Power } from 'lucide-react';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable } from '../../../components/DataTable';
import { Modal } from '../../../components/Modal';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';

const formSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters.'),
  level: z.enum(['country', 'state', 'lga', 'ward', 'community']),
});
type FormValues = z.infer<typeof formSchema>;

export function LocationsPage() {
  const canManage = usePermission('admin.locations.manage');
  const [modalOpen, setModalOpen] = useState(false);
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const locationsQuery = useQuery({ queryKey: ['locations'], queryFn: () => locationsApi.list() });

  const { register, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { level: 'state' },
  });

  const createMutation = useMutation({
    mutationFn: (values: FormValues) => locationsApi.create({ name: values.name, level: values.level, parentId: null }),
    onSuccess: () => {
      showSuccess('Location created.');
      queryClient.invalidateQueries({ queryKey: ['locations'] });
      setModalOpen(false);
      reset();
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create location.'),
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: number) => locationsApi.deactivate(id),
    onSuccess: () => {
      showSuccess('Location deactivated.');
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to deactivate location.'),
  });

  return (
    <div className="page">
      <PageHeader title="Locations" actions={canManage && <button className="btn-primary" onClick={() => setModalOpen(true)}>New Location</button>} />

      {locationsQuery.isLoading && <p>Loading…</p>}
      {locationsQuery.isError && <p className="error-text">Could not load locations.</p>}

      {locationsQuery.data && (
        <DataTable
          rows={locationsQuery.data.data}
          rowKey={(l) => l.id}
          emptyMessage="No locations yet."
          columns={[
            { header: 'Name', render: (l) => l.name },
            { header: 'Level', render: (l) => l.level },
            { header: 'Status', render: (l) => (l.is_active ? 'Active' : 'Inactive') },
            {
              header: '',
              render: (l) =>
                canManage && l.is_active ? (
                  <IconActionButton icon={Power} label="Deactivate" onClick={() => deactivateMutation.mutate(l.id)} />
                ) : null,
            },
          ]}
        />
      )}

      {modalOpen && (
        <Modal title="New Location" onClose={() => setModalOpen(false)}>
          <form onSubmit={handleSubmit((values) => createMutation.mutate(values))}>
            <label>
              Name
              <input {...register('name')} />
              {errors.name && <span className="field-error">{errors.name.message}</span>}
            </label>
            <label>
              Level
              <select {...register('level')}>
                <option value="country">Country</option>
                <option value="state">State</option>
                <option value="lga">LGA</option>
                <option value="ward">Ward</option>
                <option value="community">Community</option>
              </select>
            </label>
            <div className="form-actions">
              <button type="submit" className="btn-primary" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Saving…' : 'Create'}
              </button>
            <button type="button" className="btn-secondary" onClick={() => setModalOpen(false)}>Cancel</button></div>
          </form>
        </Modal>
      )}
    </div>
  );
}
